import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Send, Loader2, X, Plus, ArrowUpRight, Bell, BellOff, RefreshCw, Search, Briefcase, Paperclip, Archive, ArchiveRestore, FileText, Inbox } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { enablePush, pushStatus, pushSupported } from "../../lib/push";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (u) => (u && u.startsWith("/api") ? `${BACKEND_URL}${u}` : u);
const inp = "w-full px-3 py-2 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep";

const linkify = (text) => {
  const parts = (text || "").split(/(https?:\/\/[^\s]+|\/reserver-creneau\/[A-Za-z0-9]+)/g);
  return parts.map((p, i) => {
    if (/^https?:\/\//.test(p) || /^\/reserver-creneau\//.test(p)) {
      return <a key={i} href={p} target={p.startsWith("http") ? "_blank" : "_self"} rel="noreferrer" className="underline font-medium break-all text-brand-greenDeep">{p}</a>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

const Attachments = ({ list }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {(list || []).map((a, i) => a.is_image ? (
      <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer" data-testid={`msg-attach-img-${i}`}>
        <img src={fileUrl(a.url)} alt={a.filename} className="rounded-lg max-h-40 border border-brand-soft object-cover" />
      </a>
    ) : (
      <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs bg-white/70 border border-brand-soft rounded-lg px-2.5 py-1.5 text-brand-greenDeep hover:bg-white" data-testid={`msg-attach-file-${i}`}>
        <FileText className="w-3.5 h-3.5" /> <span className="truncate max-w-[180px]">{a.filename}</span>
      </a>
    ))}
  </div>
);

function PushButton() {
  const [status, setStatus] = useState("off");
  const [busy, setBusy] = useState(false);
  useEffect(() => { pushStatus().then(setStatus); }, []);
  if (!pushSupported()) return null;
  const activate = async () => {
    setBusy(true);
    try { await enablePush(); setStatus("on"); toast.success("Notifications push activées"); }
    catch (e) { toast.error(e.message || "Activation impossible"); }
    finally { setBusy(false); }
  };
  const test = async () => { try { const r = await api.post("/push/test"); toast.success(r.data.sent > 0 ? "Notification envoyée" : "Aucun appareil abonné"); } catch { toast.error("Erreur"); } };
  if (status === "on") return <button onClick={test} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-lime/40 text-brand-greenDeep text-sm font-semibold" data-testid="push-test-btn"><Bell className="w-4 h-4" /> Notifications activées</button>;
  if (status === "denied") return <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-50 text-red-600 text-sm font-semibold"><BellOff className="w-4 h-4" /> Notifications bloquées</span>;
  return <button onClick={activate} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold disabled:opacity-60" data-testid="push-enable-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Activer les notifications</button>;
}

export default function ReferentMessagerie() {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [unread, setUnread] = useState(0);
  const [archivedKeys, setArchivedKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [compose, setCompose] = useState(false);
  const [form, setForm] = useState({ to: "", subject: "", body: "" });
  const [attachFiles, setAttachFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeConv, setActiveConv] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("app");
  const fileRef = useRef(null);

  const load = () => api.get("/referents/emails").then((r) => {
    setEmails(r.data.emails || []); setUnread(r.data.unread || 0); setArchivedKeys(r.data.archived_keys || []);
  }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const conversations = useMemo(() => {
    const map = {};
    emails.forEach((e) => {
      const key = ((e.direction === "in" ? e.from : e.to) || "inconnu").toLowerCase();
      if (!map[key]) map[key] = { key, name: e.direction === "in" ? e.from : e.to, messages: [], unread: 0, lastAt: "", missionId: "", channel: "app" };
      map[key].messages.push(e);
      if (e.mission_id) map[key].missionId = e.mission_id;
      if (e.direction === "in" && !e.read) map[key].unread += 1;
      if ((e.created_at || "") >= map[key].lastAt) { map[key].lastAt = e.created_at || ""; map[key].channel = e.channel === "email" ? "email" : "app"; }
    });
    const list = Object.values(map);
    list.forEach((c) => c.messages.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")));
    return list.sort((a, b) => (b.lastAt || "").localeCompare(a.lastAt || ""));
  }, [emails]);

  const counts = useMemo(() => {
    let app = 0, email = 0, archived = 0;
    conversations.forEach((c) => {
      if (archivedKeys.includes(c.key)) { archived += 1; return; }
      if (c.channel === "email") email += 1; else app += 1;
    });
    return { app, email, archived };
  }, [conversations, archivedKeys]);

  const active = conversations.find((c) => c.key === activeConv);
  const filteredConvs = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = conversations.filter((c) => {
      const isArch = archivedKeys.includes(c.key);
      if (tab === "archived") return isArch;
      if (isArch) return false;
      return tab === "app" ? c.channel !== "email" : c.channel === "email";
    });
    if (s) list = list.filter((c) => (c.name || "").toLowerCase().includes(s) || c.messages.some((m) => (m.subject || "").toLowerCase().includes(s) || (m.body || "").toLowerCase().includes(s)));
    return list;
  }, [conversations, q, tab, archivedKeys]);

  const openConv = async (c) => {
    setActiveConv(c.key);
    const unreadIn = c.messages.filter((m) => m.direction === "in" && !m.read);
    if (unreadIn.length) { try { await Promise.all(unreadIn.map((m) => api.post(`/referents/emails/${m.id}/read`))); load(); } catch {} }
  };

  const archiveConv = async (c, archived) => {
    try {
      await api.post("/referents/emails/archive", { key: c.key, archived });
      setArchivedKeys((prev) => archived ? Array.from(new Set([...prev, c.key])) : prev.filter((k) => k !== c.key));
      if (activeConv === c.key) setActiveConv(null);
      toast.success(archived ? "Conversation archivée" : "Conversation désarchivée");
    } catch { toast.error("Action impossible"); }
  };

  const openEmail = async (e) => {
    setOpen(e);
    if (e.direction === "in" && !e.read) { try { await api.post(`/referents/emails/${e.id}/read`); load(); } catch {} }
  };
  const reply = (e) => { setForm({ to: e.from || "", subject: e.subject?.startsWith("Re:") ? e.subject : `Re: ${e.subject}`, body: "" }); setAttachFiles([]); setCompose(true); setOpen(null); };
  const replyConv = (c) => { const last = c.messages[c.messages.length - 1]; setForm({ to: c.name || "", subject: last?.subject?.startsWith("Re:") ? last.subject : `Re: ${last?.subject || ""}`, body: "" }); setAttachFiles([]); setCompose(true); };

  const send = async () => {
    if (!form.to.trim() || (!form.body.trim() && attachFiles.length === 0)) { toast.error("Destinataire et message (ou pièce jointe) obligatoires"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("to", form.to); fd.append("cc", ""); fd.append("subject", form.subject || ""); fd.append("body", form.body || ""); fd.append("thread_id", "");
      attachFiles.forEach((f) => fd.append("files", f));
      const r = await api.post("/referents/emails", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.channel === "app" ? "Message livré dans l'application du destinataire" : "Message envoyé par e-mail (simulé)");
      setCompose(false); setForm({ to: "", subject: "", body: "" }); setAttachFiles([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const TABS = [
    { key: "app", label: "App in", hint: "dans l'application", count: counts.app },
    { key: "email", label: "App out", hint: "par e-mail", count: counts.email },
    { key: "archived", label: "Archivés", hint: "", count: counts.archived },
  ];

  return (
    <div className="space-y-6" data-testid="referent-messagerie-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><Mail className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Messagerie</h2>
            <p className="text-sm text-brand-green/55">Échangez avec les propriétaires et locataires{unread > 0 ? ` · ${unread} non lu(s)` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PushButton />
          <button onClick={load} className="p-2.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep" title="Actualiser" data-testid="msg-refresh"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => { setForm({ to: "", subject: "", body: "" }); setAttachFiles([]); setCompose(true); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid="msg-compose-btn"><Plus className="w-4 h-4" /> Nouveau message</button>
        </div>
      </div>

      <div className="bg-brand-lime/10 border border-brand-lime/40 rounded-2xl px-4 py-2.5 text-xs text-brand-green/70" data-testid="msg-simulated-banner">
        <b>App in</b> = messages livrés directement dans l'application (le destinataire y possède un compte). <b>App out</b> = messages envoyés par e-mail (envoi simulé en démo). Les réponses in-app arrivent ici automatiquement.
      </div>

      {/* Onglets par canal + archivés */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="msg-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setActiveConv(null); }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-brand-greenDeep text-white" : "bg-brand-softer text-brand-green/70 hover:bg-brand-soft"}`}
            data-testid={`msg-tab-${t.key}`}>
            {t.key === "app" ? <Inbox className="w-4 h-4" /> : t.key === "email" ? <ArrowUpRight className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
            {t.label}
            <span className={`text-[11px] rounded-full px-1.5 py-0.5 ${tab === t.key ? "bg-white/20" : "bg-white"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-brand-green/50"><Loader2 className="w-7 h-7 animate-spin" /></div>
      ) : active ? (
        <div className="bg-white rounded-3xl border border-brand-soft overflow-hidden" data-testid="msg-thread">
          <div className="flex items-center justify-between px-5 py-3 border-b border-brand-soft bg-brand-softer/50">
            <button onClick={() => setActiveConv(null)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-greenDeep" data-testid="msg-thread-back"><ArrowUpRight className="w-4 h-4 rotate-180" /> Conversations</button>
            <span className="text-sm font-semibold text-brand-greenDeep truncate">{active.name} <span className="text-[11px] font-normal text-brand-green/40">· {active.channel === "email" ? "app out" : "app in"}</span></span>
            <div className="flex items-center gap-2">
              {active.missionId && <button onClick={() => navigate(`/app/referent-missions?mission=${active.missionId}`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-xs font-semibold" data-testid="msg-view-mission-btn"><Briefcase className="w-3.5 h-3.5" /> Voir la mission liée</button>}
              {archivedKeys.includes(active.key)
                ? <button onClick={() => archiveConv(active, false)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-xs font-semibold" data-testid="msg-unarchive-btn"><ArchiveRestore className="w-3.5 h-3.5" /> Désarchiver</button>
                : <button onClick={() => archiveConv(active, true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-xs font-semibold" data-testid="msg-archive-btn"><Archive className="w-3.5 h-3.5" /> Archiver</button>}
              <button onClick={() => replyConv(active)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-greenDeep text-white text-xs font-semibold" data-testid="msg-thread-reply"><Send className="w-3.5 h-3.5" /> Répondre</button>
            </div>
          </div>
          <div className="p-4 space-y-3 max-h-[55vh] overflow-y-auto">
            {active.messages.map((e) => (
              <div key={e.id} className={`max-w-[85%] rounded-2xl p-3 ${e.direction === "out" ? "ml-auto bg-brand-lime/20" : "bg-brand-softer"}`} data-testid={`msg-bubble-${e.id}`}>
                <div className="flex items-center justify-between gap-3"><span className="text-[11px] font-semibold text-brand-greenDeep">{e.direction === "out" ? "Vous" : e.from}{e.cc ? ` · cc ${e.cc}` : ""}</span><span className="text-[10px] text-brand-green/40">{e.channel === "app" ? "app" : "e-mail"} · {(e.created_at || "").slice(0, 16).replace("T", " ")}</span></div>
                {e.subject && <p className="text-xs font-semibold text-brand-green/70 mt-0.5">{e.subject}</p>}
                {e.body && <p className="text-sm text-brand-green/80 whitespace-pre-wrap break-words mt-0.5">{linkify(e.body)}</p>}
                {(e.attachments || []).length > 0 && <Attachments list={e.attachments} />}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative" data-testid="msg-search-box">
            <Search className="w-4 h-4 text-brand-green/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une conversation ou un message…" className="w-full pl-9 pr-3 py-2.5 rounded-2xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep" data-testid="msg-search-input" />
          </div>
          <div className="bg-white rounded-3xl border border-brand-soft overflow-hidden" data-testid="msg-list">
            {filteredConvs.length === 0 && <p className="px-5 py-10 text-center text-sm text-brand-green/45" data-testid="msg-empty">{q ? `Aucune conversation ne correspond à « ${q} ».` : tab === "archived" ? "Aucune conversation archivée." : tab === "app" ? "Aucune conversation dans l'application." : "Aucun échange par e-mail."}</p>}
            {filteredConvs.map((c) => { const last = c.messages[c.messages.length - 1]; const isArch = archivedKeys.includes(c.key); return (
            <div key={c.key} className={`w-full px-5 py-4 border-b border-brand-soft last:border-0 hover:bg-brand-softer/50 transition-colors flex items-center gap-3 ${c.unread ? "bg-brand-lime/10" : ""}`} data-testid={`msg-conv-${c.key}`}>
              <button onClick={() => openConv(c)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                <span className="w-9 h-9 rounded-full bg-brand-softer text-brand-greenDeep flex items-center justify-center shrink-0 font-semibold text-sm">{(c.name || "?").charAt(0).toUpperCase()}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm truncate ${c.unread ? "font-bold text-brand-greenDeep" : "font-medium text-brand-greenDeep"}`}>{c.name}</p>
                    <span className={`text-[10px] rounded-full px-1.5 py-0.5 shrink-0 ${c.channel === "email" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{c.channel === "email" ? "app out" : "app in"}</span>
                    <span className="text-[11px] font-normal text-brand-green/40 shrink-0">· {c.messages.length} msg</span>
                  </div>
                  <p className="text-sm text-brand-green/60 truncate">{last?.direction === "out" ? "Vous : " : ""}{last?.subject}</p>
                  <p className="text-xs text-brand-green/40 truncate">{last?.body || ((last?.attachments || []).length ? `📎 ${last.attachments.length} pièce(s) jointe(s)` : "")}</p>
                </div>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[11px] text-brand-green/45 hidden sm:block">{(c.lastAt || "").slice(0, 16).replace("T", " ")}</span>
                {c.unread > 0 && <span className="text-[10px] font-bold bg-brand-limeDark text-brand-greenDeep rounded-full px-2 py-0.5" data-testid={`msg-conv-unread-${c.key}`}>{c.unread}</span>}
                {isArch
                  ? <button onClick={() => archiveConv(c, false)} title="Désarchiver" className="p-1.5 rounded-full hover:bg-brand-softer text-brand-green/50 hover:text-brand-greenDeep" data-testid={`msg-conv-unarchive-${c.key}`}><ArchiveRestore className="w-4 h-4" /></button>
                  : <button onClick={() => archiveConv(c, true)} title="Archiver" className="p-1.5 rounded-full hover:bg-brand-softer text-brand-green/50 hover:text-brand-greenDeep" data-testid={`msg-conv-archive-${c.key}`}><Archive className="w-4 h-4" /></button>}
              </div>
            </div>
          ); })}
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="msg-read-modal">
            <div className="flex items-center justify-between mb-2"><h3 className="font-display text-xl font-semibold text-brand-greenDeep">{open.subject}</h3><button onClick={() => setOpen(null)}><X className="w-5 h-5 text-brand-green/50" /></button></div>
            <p className="text-xs text-brand-green/55">{open.direction === "in" ? `De : ${open.from}` : `À : ${open.to}`} · {(open.created_at || "").slice(0, 16).replace("T", " ")}{open.simulated ? " · (envoi simulé)" : ""}</p>
            <div className="mt-4 text-sm text-brand-green/80 whitespace-pre-wrap break-words bg-brand-softer rounded-xl p-4" data-testid="msg-read-body">{linkify(open.body)}</div>
            {(open.attachments || []).length > 0 && <Attachments list={open.attachments} />}
            <div className="mt-4 flex justify-end"><button onClick={() => reply(open)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep text-white text-sm font-semibold" data-testid="msg-reply-btn"><Send className="w-4 h-4" /> Répondre</button></div>
          </div>
        </div>
      )}

      {compose && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCompose(false)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="msg-compose-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-display text-xl font-semibold text-brand-greenDeep">Nouveau message</h3><button onClick={() => setCompose(false)}><X className="w-5 h-5 text-brand-green/50" /></button></div>
            <div className="space-y-3">
              <input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} type="email" placeholder="Destinataire (e-mail)" className={inp} data-testid="msg-to" />
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Objet" className={inp} data-testid="msg-subject" />
              <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} placeholder="Votre message…" className={inp} data-testid="msg-body" />

              <input ref={fileRef} type="file" multiple className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" onChange={(e) => { const fs = Array.from(e.target.files || []); setAttachFiles((prev) => [...prev, ...fs].slice(0, 6)); e.target.value = ""; }} data-testid="msg-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-brand-soft hover:bg-brand-softer text-sm text-brand-greenDeep font-semibold" data-testid="msg-attach-btn"><Paperclip className="w-4 h-4" /> Ajouter un fichier</button>
              {attachFiles.length > 0 && (
                <div className="space-y-1.5" data-testid="msg-attach-list">
                  {attachFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-brand-softer rounded-xl px-3 py-2 text-sm text-brand-greenDeep" data-testid={`msg-attach-item-${i}`}>
                      <FileText className="w-4 h-4 shrink-0" /><span className="truncate flex-1">{f.name}</span>
                      <button type="button" onClick={() => setAttachFiles((prev) => prev.filter((_, j) => j !== i))} className="text-brand-green/40 hover:text-red-500" data-testid={`msg-attach-remove-${i}`}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={send} disabled={busy} className="w-full py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="msg-send-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
